import { useState } from "react";
import { KeyRound, Mail, ShieldCheck, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";

export default function Profile() {
  const { user } = useAuth();
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.next.length < 8) { toast.error("Le nouveau mot de passe doit contenir au moins 8 caractères"); return; }
    if (form.next !== form.confirm) { toast.error("La confirmation ne correspond pas au nouveau mot de passe"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current, new_password: form.next });
      toast.success("Mot de passe modifié — il est actif immédiatement et ne sera jamais écrasé par un déploiement.");
      setForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la modification");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="profile-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Mon profil</h1>
        <p className="text-sm text-neutral-500 mt-1">Vos informations de compte et la sécurité de votre accès.</p>
      </div>

      <div className="bg-white border border-neutral-200 rounded-xl shadow-sm p-6 space-y-3" data-testid="profile-info">
        <h2 className="font-heading font-bold">Informations</h2>
        <div className="flex items-center gap-3 text-sm">
          <User className="w-4 h-4 text-neutral-400" />
          <span className="text-neutral-500 w-28">Nom</span>
          <span className="font-medium" data-testid="profile-name">{user?.name}</span>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <Mail className="w-4 h-4 text-neutral-400" />
          <span className="text-neutral-500 w-28">Email</span>
          <span className="font-medium" data-testid="profile-email">{user?.email}</span>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <ShieldCheck className="w-4 h-4 text-neutral-400" />
          <span className="text-neutral-500 w-28">Rôle</span>
          <span className="font-medium" data-testid="profile-role">{user?.role === "owner" ? "Propriétaire" : user?.role}</span>
        </div>
      </div>

      <form onSubmit={submit} className="bg-white border border-neutral-200 rounded-xl shadow-sm p-6 space-y-4" data-testid="profile-password-form">
        <div>
          <h2 className="font-heading font-bold flex items-center gap-2"><KeyRound className="w-4 h-4" /> Changer mon mot de passe</h2>
          <p className="text-xs text-neutral-400 mt-1">Le changement est immédiat et persiste après chaque déploiement.</p>
        </div>
        <div>
          <Label className="text-xs">Mot de passe actuel *</Label>
          <Input type="password" className="mt-1" value={form.current} required autoComplete="current-password"
            onChange={(e) => setForm({ ...form, current: e.target.value })} data-testid="profile-current-password" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Nouveau mot de passe *</Label>
            <Input type="password" className="mt-1" value={form.next} required minLength={8} autoComplete="new-password"
              onChange={(e) => setForm({ ...form, next: e.target.value })} data-testid="profile-new-password" />
          </div>
          <div>
            <Label className="text-xs">Confirmation *</Label>
            <Input type="password" className="mt-1" value={form.confirm} required autoComplete="new-password"
              onChange={(e) => setForm({ ...form, confirm: e.target.value })} data-testid="profile-confirm-password" />
          </div>
        </div>
        <Button type="submit" disabled={busy} className="bg-neutral-900 hover:bg-neutral-800 text-white" data-testid="profile-password-submit">
          {busy ? "Modification…" : "Modifier mon mot de passe"}
        </Button>
      </form>
    </div>
  );
}
